import type { GenericObjectType } from '@rjsf/utils';

/**
 * Generic object traversal function that handles common path building and iteration logic
 * Uses array-based paths to correctly handle property names containing dots
 * @param obj - The object to traverse
 * @param basePath - The base path array for building field paths
 * @param handlers - Handler functions for different value types
 * @returns Array of field paths (as arrays)
 */
function traverseObjectPaths(
  obj: unknown,
  basePath: PropertyKey[],
  handlers: {
    handleArray: (value: unknown[], currentPath: PropertyKey[]) => PropertyKey[][];
    handleObject: (value: GenericObjectType, currentPath: PropertyKey[]) => PropertyKey[][];
    handleLeaf?: (value: unknown, currentPath: PropertyKey[]) => PropertyKey[][];
  },
): PropertyKey[][] {
  if (!obj || typeof obj !== 'object') return handlers.handleLeaf?.(obj, basePath) ?? [];

  const paths: PropertyKey[][] = [];
  const objRecord = obj as GenericObjectType;

  for (const key in objRecord) {
    if (!Object.hasOwn(objRecord, key)) continue;

    const currentPath = [
      ...basePath,
      key,
    ];
    const value = objRecord[key];

    if (Array.isArray(value)) {
      paths.push(...handlers.handleArray(value, currentPath));
    } else if (typeof value === 'object' && value !== null) {
      paths.push(...handlers.handleObject(value as GenericObjectType, currentPath));
    } else {
      paths.push(...(handlers.handleLeaf?.(value, currentPath) ?? []));
    }
  }

  return paths;
}

/**
 * Extract all leaf field paths from a newly created object structure
 * @param obj - The new object structure
 * @param basePath - The base path array for the object
 * @returns Array of all leaf field paths in the object (as arrays)
 */
const extractLeafPaths = (obj: unknown, basePath: PropertyKey[]): PropertyKey[][] =>
  traverseObjectPaths(obj, basePath, {
    // Handle arrays - extract paths for each element
    handleArray: (value, currentPath) =>
      value.flatMap((item, index) =>
        extractLeafPaths(item, [
          ...currentPath,
          index,
        ]),
      ),
    // This is a leaf value
    handleLeaf: (_value, currentPath) => [
      currentPath,
    ],
    // Recurse into nested objects
    handleObject: (value, currentPath) => extractLeafPaths(value, currentPath),
  });

/**
 * Internal recursive function that extracts changed paths as arrays
 */
const extractChangedPathsInternal = (delta: GenericObjectType, basePath: PropertyKey[]): PropertyKey[][] =>
  traverseObjectPaths(delta, basePath, {
    handleArray: (value, currentPath) => {
      if (value.length === 1) {
        // [newValue] - this is a newly created object/structure
        // Extract all leaf paths from the new structure
        return extractLeafPaths(value[0], currentPath);
      }
      if (value.length <= 3) {
        // [oldValue, newValue] (3) - this is a direct field change
        // [oldValue, 0, 0] (2) - this is a deletion
        return [
          currentPath,
        ];
      }
      return [];
    },
    // This is a nested object, recurse
    handleObject: (value, currentPath) => extractChangedPathsInternal(value as GenericObjectType, currentPath),
  });

/**
 * Extract changed field paths from jsondiffpatch delta as array paths
 * Array paths correctly handle property names that contain dots
 * @param delta - The delta object from jsondiffpatch
 * @param basePath - The base path for nested objects (empty string for root)
 * @returns Array of field paths that changed (as string arrays)
 */
export const extractChangedPathsAsArrays = (delta: GenericObjectType): PropertyKey[][] => extractChangedPathsInternal(delta, []);
